import { z } from "zod";

// TES-1554 (Ö-860–Ö-863, Emelie and Zeinab 2026-10-22) — when the doctor ends
// the drug treatment because the patient is no longer suitable for it, the
// subscription ends from that date and the paid days after it are refunded.
//
// Ö-861, verbatim: "Återbetalning räknas proportionellt per dag från läkarens
// dokumenterade avslutsdatum, inklusive den dagen, till den betalda periodens
// slut. Endast faktiskt betalt belopp återbetalas. En helt kostnadsfri period
// ger ingen återbetalning."
//
// Ö-863: "Manuell hantering av admin i första versionen. Läkarens
// dokumenterade utfall skapar en uppgift med avslutsdatum och beräknat
// återbetalningsbelopp. Admin verkställer avslut och återbetalning. Undantaget
// från bindnings- och uppsägningstid gäller från avslutsdatumet även om
// administrationen sker senare."
//
// The calculation is pure and works on calendar dates in the tenant's time
// zone (YYYY-MM-DD); the API turns Stripe's period instants into those dates.

export const SUBSCRIPTION_END_REFUND_QUEUE_LABEL = "Avslut och återbetalning";
export const SUBSCRIPTION_END_REFUND_TITLE =
  "Avsluta abonnemanget och återbetala";

/** One paid invoice, as the refund needs it. */
export interface RefundInvoiceInput {
  /** What she actually paid for it, in the smallest currency unit. */
  amountPaidMinor: number;
  /** First calendar day the invoice paid for (tenant time zone). */
  periodStartDate: string;
  /**
   * The calendar day the paid period ends on, exclusive: the day the next
   * period starts (Stripe's period end, in the tenant's time zone).
   */
  periodEndDate: string;
}

export interface RefundLine {
  periodStartDate: string;
  periodEndDate: string;
  amountPaidMinor: number;
  /** Calendar days the invoice paid for. */
  periodDays: number;
  /** Of those, the days from the end date (inclusive) to the period's end. */
  refundDays: number;
  refundMinor: number;
}

export interface RefundCalculation {
  /**
   * Null when the paid amount for the end date's period is not known (no
   * recorded invoice covers it and the subscription is not in a free trial):
   * admin then reads the amount in Stripe. Never a guess.
   */
  refundMinor: number | null;
  lines: RefundLine[];
}

const DAY_MS = 86_400_000;

/** Whole calendar days from `from` to `to` (both YYYY-MM-DD). */
export function calendarDaysBetween(from: string, to: string): number {
  return Math.round(
    (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS,
  );
}

/**
 * The refund for one paid invoice: its paid amount pro rata per day, counted
 * from the end date (inclusive) to the end of the paid period. A period that
 * ended before the end date gives 0; a period that starts after it (a
 * renewal charged before admin acted) is refunded in full. Never more than
 * was paid: the share is at most the whole period.
 */
export function refundLine(
  endDate: string,
  invoice: RefundInvoiceInput,
): RefundLine {
  const periodDays = Math.max(
    0,
    calendarDaysBetween(invoice.periodStartDate, invoice.periodEndDate),
  );
  const from =
    endDate > invoice.periodStartDate ? endDate : invoice.periodStartDate;
  const refundDays = Math.min(
    periodDays,
    Math.max(0, calendarDaysBetween(from, invoice.periodEndDate)),
  );
  const paid = Math.max(0, invoice.amountPaidMinor);
  const refundMinor =
    periodDays === 0 ? 0 : Math.round((paid * refundDays) / periodDays);
  return {
    periodStartDate: invoice.periodStartDate,
    periodEndDate: invoice.periodEndDate,
    amountPaidMinor: paid,
    periodDays,
    refundDays,
    refundMinor,
  };
}

/**
 * The refund for an end on `endDate`, over every paid invoice of the
 * subscription that reaches past it.
 *
 * `freePeriod` is true when the subscription is in a free trial: with no
 * recorded invoice for the end date's period the refund is then 0 (Ö-861: "En
 * helt kostnadsfri period ger ingen återbetalning"). A 0 kr invoice (a 100 %
 * code) gives 0 on its own. Without either, an end date that no recorded
 * invoice covers makes the amount unknown (null).
 */
export function calculateEndRefund(input: {
  endDate: string;
  invoices: readonly RefundInvoiceInput[];
  freePeriod: boolean;
}): RefundCalculation {
  const lines = input.invoices
    .map((inv) => refundLine(input.endDate, inv))
    .filter((l) => l.refundDays > 0)
    .sort((a, b) => a.periodStartDate.localeCompare(b.periodStartDate));
  const covered = input.invoices.some(
    (inv) =>
      inv.periodStartDate <= input.endDate && input.endDate < inv.periodEndDate,
  );
  if (!covered && !input.freePeriod) {
    return { refundMinor: null, lines };
  }
  return {
    refundMinor: lines.reduce((sum, l) => sum + l.refundMinor, 0),
    lines,
  };
}

/** "1 234,50 kr" — minor units as Swedish kronor. */
export function formatSekMinor(minor: number): string {
  const kr = minor / 100;
  return `${kr.toLocaleString("sv-SE", {
    minimumFractionDigits: Number.isInteger(kr) ? 0 : 2,
    maximumFractionDigits: 2,
  })} kr`;
}

// ---- The admin's list ------------------------------------------------------

export const refundLineSchema = z.object({
  periodStartDate: z.string(),
  periodEndDate: z.string(),
  amountPaidMinor: z.number().int(),
  periodDays: z.number().int(),
  refundDays: z.number().int(),
  refundMinor: z.number().int(),
});

export const subscriptionEndRefundRowSchema = z.object({
  taskId: z.string().uuid(),
  patientId: z.string().uuid(),
  patientName: z.string().nullable(),
  /** The doctor's documented end date (YYYY-MM-DD). */
  endDate: z.string(),
  createdAt: z.string(),
  stripeSubscriptionId: z.string(),
  /** Stripe status as mirrored now, so admin sees whether it has ended. */
  subscriptionStatus: z.string(),
  currency: z.string(),
  /** The amount computed when the task was created. Null = see Stripe. */
  refundMinor: z.number().int().nullable(),
  lines: z.array(refundLineSchema),
  /**
   * The same calculation over the invoices known now. Differs from
   * `refundMinor` when a period was charged after the task was created (a
   * renewal before admin ended the subscription): that period is refunded
   * too.
   */
  currentRefundMinor: z.number().int().nullable(),
  /** When admin closed the task ("Stäng arbetsmoment"); null while open. */
  closedAt: z.string().nullable(),
});
export type SubscriptionEndRefundRow = z.infer<
  typeof subscriptionEndRefundRowSchema
>;

export const subscriptionEndRefundListSchema = z.object({
  /** The tenant flag: off means the doctor's outcome creates no task. */
  enabled: z.boolean(),
  rows: z.array(subscriptionEndRefundRowSchema),
});
export type SubscriptionEndRefundList = z.infer<
  typeof subscriptionEndRefundListSchema
>;
